import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Plus, Trash2, CheckCircle2, AlertTriangle, Clock, FileText, ChevronLeft, ChevronRight, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const REQUIRED_HOURS = 8;
const MIN_DESC = 20;
const HOUR_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8];

const STATUS_LABEL = {
  pending_manager: { label: 'Pending Manager', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30', icon: Clock },
  manager_approved: { label: 'Manager Approved', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30', icon: CheckCircle2 },
  manager_rejected: { label: 'Manager Rejected', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: XCircle },
  hr_approved: { label: 'HR Approved (Locked)', color: 'bg-green-500/10 text-green-700 border-green-500/30', icon: CheckCircle2 },
  hr_rejected: { label: 'HR Rejected', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: XCircle },
};

const emptyRow = () => ({ task_id: '', task_name: '', task_module: '', description: '', hours: 1, is_other: false });

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function fmtMonth(d) {
  return d.toISOString().slice(0, 7);
}

export default function SSHRTimesheetPage() {
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([emptyRow()]);
  const [lateReason, setLateReason] = useState('');
  const [tasks, setTasks] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const total = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0), [rows]);
  const remaining = REQUIRED_HOURS - total;

  const ageHours = useMemo(() => {
    const d = new Date(date + 'T00:00:00Z');
    return (Date.now() - d.getTime()) / 3600000;
  }, [date]);
  const isLate = ageHours > 24;
  const isTooLate = ageHours > 48;

  const fetchTasks = useCallback(async () => {
    try {
      const res = await api.get('/sshr/timesheet/v2/my-tasks');
      setTasks(res.data?.tasks || []);
    } catch {
      setTasks([]);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/sshr/timesheet/v2/mine?month=${month}`);
      setHistory(res.data?.items || []);
      setStats(res.data?.stats || {});
    } catch {
      setHistory([]);
      setStats({});
    } finally {
      setHistoryLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const handlePickTask = (idx, taskId) => {
    if (taskId === '__other__') {
      updateRow(idx, { task_id: '', task_name: '', task_module: 'Other', is_other: true });
      return;
    }
    const t = tasks.find(x => x.id === taskId);
    if (!t) return;
    updateRow(idx, {
      task_id: t.id,
      task_name: t.name,
      task_module: t.module || t.category || '',
      is_other: false,
    });
  };

  const addRow = () => setRows([...rows, emptyRow()]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  const validate = () => {
    if (Math.abs(total - REQUIRED_HOURS) > 0.01) {
      return `Total hours must equal exactly ${REQUIRED_HOURS}h (currently ${total}h)`;
    }
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.task_name?.trim() && !r.is_other) return `Row ${i + 1}: select a task or "Other"`;
      if (r.is_other && !r.task_name?.trim()) return `Row ${i + 1}: enter a task name for "Other"`;
      if ((r.description || '').trim().length < MIN_DESC) {
        return `Row ${i + 1}: description must be at least ${MIN_DESC} chars`;
      }
      if (!r.hours || r.hours <= 0) return `Row ${i + 1}: hours required`;
    }
    if (isLate && !lateReason.trim()) return 'Late submission requires a reason';
    if (isTooLate) return 'Date is older than 48 hours — late submissions are no longer accepted';
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await api.post('/sshr/timesheet/v2', {
        date,
        rows: rows.map(r => ({
          task_id: r.task_id || null,
          task_name: r.task_name,
          task_module: r.task_module || null,
          description: r.description,
          hours: Number(r.hours),
          is_other: !!r.is_other,
        })),
        late_reason: isLate ? lateReason.trim() : null,
      });
      toast.success(isLate ? 'Timesheet submitted (LATE) — pending manager review' : 'Timesheet submitted — pending manager review');
      setRows([emptyRow()]);
      setLateReason('');
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const monthDate = new Date(month + '-01');
  const prevMonth = () => {
    const d = new Date(monthDate); d.setMonth(d.getMonth() - 1); setMonth(fmtMonth(d));
  };
  const nextMonth = () => {
    const d = new Date(monthDate); d.setMonth(d.getMonth() + 1); setMonth(fmtMonth(d));
  };
  const monthLabel = monthDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="sshr-timesheet-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">My Timesheet</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Log exactly 8 hours per working day. Late submissions allowed within 48 hours with reason.
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" data-testid="ts-stat-approved">Approved: {stats.approved || 0}</Badge>
          <Badge variant="outline" data-testid="ts-stat-pending">Pending: {stats.pending || 0}</Badge>
          <Badge variant="outline" data-testid="ts-stat-rejected">Rejected: {stats.rejected || 0}</Badge>
        </div>
      </div>

      {/* Submission Form */}
      <Card data-testid="ts-submit-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5" /> Daily Submission
              </CardTitle>
              <CardDescription>
                Map every hour to a task from your queue or pick &quot;Other&quot; for ad-hoc work.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Date</label>
              <Input
                type="date"
                value={date}
                max={todayStr()}
                onChange={(e) => setDate(e.target.value)}
                className="w-[160px]"
                data-testid="ts-date-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLate && !isTooLate && (
            <div className="flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Late submission ({Math.floor(ageHours)}h after the date)</p>
                <Input
                  placeholder="Reason for late submission (required)"
                  value={lateReason}
                  onChange={(e) => setLateReason(e.target.value)}
                  className="mt-2 bg-background"
                  data-testid="ts-late-reason"
                />
              </div>
            </div>
          )}
          {isTooLate && (
            <div className="flex items-center gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4" />
              This date is &gt;48h old. Late window expired — request HR override instead.
            </div>
          )}

          <div className="space-y-3">
            {rows.map((row, idx) => {
              const taskValue = row.is_other ? '__other__' : (row.task_id || '');
              const descLen = (row.description || '').trim().length;
              return (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-start p-3 rounded-lg border bg-muted/20"
                  data-testid={`ts-row-${idx}`}
                >
                  <div className="col-span-12 md:col-span-4">
                    <label className="text-[11px] text-muted-foreground">Task</label>
                    <Select value={taskValue} onValueChange={(v) => handlePickTask(idx, v)}>
                      <SelectTrigger className="mt-1" data-testid={`ts-row-task-${idx}`}>
                        <SelectValue placeholder="Pick a task..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tasks.length === 0 && (
                          <div className="px-2 py-2 text-xs text-muted-foreground">No assigned tasks. Use &quot;Other&quot;.</div>
                        )}
                        {tasks.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            <div className="flex flex-col">
                              <span className="text-sm truncate max-w-[240px]">{t.name}</span>
                              {t.module && <span className="text-[10px] text-muted-foreground">{t.module}</span>}
                            </div>
                          </SelectItem>
                        ))}
                        <SelectItem value="__other__">
                          <span className="font-medium">Other (manual entry)</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {row.is_other && (
                      <Input
                        placeholder="Custom task name"
                        value={row.task_name}
                        onChange={(e) => updateRow(idx, { task_name: e.target.value })}
                        className="mt-1 text-sm"
                        data-testid={`ts-row-other-name-${idx}`}
                      />
                    )}
                  </div>

                  <div className="col-span-8 md:col-span-6">
                    <label className="text-[11px] text-muted-foreground">
                      Description ({descLen}/{MIN_DESC} min)
                    </label>
                    <Textarea
                      value={row.description}
                      onChange={(e) => updateRow(idx, { description: e.target.value })}
                      placeholder="Specifically what did you do? (min 20 chars)"
                      className="mt-1 min-h-[60px] text-sm"
                      data-testid={`ts-row-desc-${idx}`}
                    />
                  </div>

                  <div className="col-span-3 md:col-span-1">
                    <label className="text-[11px] text-muted-foreground">Hours</label>
                    <Select
                      value={String(row.hours)}
                      onValueChange={(v) => updateRow(idx, { hours: parseFloat(v) })}
                    >
                      <SelectTrigger className="mt-1" data-testid={`ts-row-hours-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOUR_OPTIONS.map(h => (
                          <SelectItem key={h} value={String(h)}>{h}h</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-1 flex justify-end">
                    {rows.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 mt-5"
                        onClick={() => removeRow(idx)}
                        data-testid={`ts-row-delete-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={addRow} data-testid="ts-add-row">
              <Plus className="h-4 w-4 mr-1" /> Add Row
            </Button>
            <div className="flex items-center gap-3">
              <div className="text-sm">
                <span className="text-muted-foreground">Total: </span>
                <span className={`font-bold ${Math.abs(total - REQUIRED_HOURS) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
                  {total.toFixed(1)}h / {REQUIRED_HOURS}h
                </span>
                {remaining > 0 && (
                  <span className="text-xs text-amber-600 ml-2">({remaining.toFixed(1)}h short)</span>
                )}
                {remaining < 0 && (
                  <span className="text-xs text-red-600 ml-2">({Math.abs(remaining).toFixed(1)}h over)</span>
                )}
              </div>
              <Button
                onClick={submit}
                disabled={submitting || isTooLate}
                data-testid="ts-submit-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {submitting ? 'Submitting…' : 'Submit for Approval'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">My Submissions — {monthLabel}</CardTitle>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="ts-month-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="ts-month-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No timesheets for this month yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="pb-2 font-medium">Date</th>
                    <th className="pb-2 font-medium">Hours</th>
                    <th className="pb-2 font-medium">Tasks</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(item => {
                    const s = STATUS_LABEL[item.status] || { label: item.status, color: 'bg-muted', icon: Clock };
                    const Icon = s.icon;
                    const comment = item.hr_comment || item.manager_comment;
                    return (
                      <tr
                        key={item.id}
                        className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                        onClick={() => setSelected(item)}
                        data-testid={`ts-history-row-${item.date}`}
                      >
                        <td className="py-2 font-medium">
                          {item.date}
                          {item.is_late && <Badge variant="destructive" className="ml-2 text-[9px] py-0 px-1">LATE</Badge>}
                        </td>
                        <td className="py-2">{item.total_hours}h</td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {(item.rows || []).length} task(s)
                        </td>
                        <td className="py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border ${s.color}`}>
                            <Icon className="h-3 w-3" /> {s.label}
                          </span>
                        </td>
                        <td className="py-2 text-xs text-muted-foreground truncate max-w-[260px]">{comment || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-md bg-background h-full overflow-y-auto p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="ts-detail-drawer"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold">{selected.date}</h3>
                <p className="text-xs text-muted-foreground">
                  Total {selected.total_hours}h · Submitted {new Date(selected.submitted_at).toLocaleString()}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelected(null)}>
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
            <div className="space-y-3">
              {(selected.rows || []).map((r, i) => (
                <div key={i} className="p-3 rounded-md border bg-muted/30">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-sm">{r.task_name}</p>
                    <Badge variant="outline">{r.hours}h</Badge>
                  </div>
                  {r.task_module && <Badge variant="secondary" className="text-[10px] mb-1">{r.task_module}</Badge>}
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{r.description}</p>
                </div>
              ))}
              {selected.is_late && (
                <div className="p-3 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-700 text-xs">
                  <p className="font-medium">Late Submission Reason:</p>
                  <p>{selected.late_reason || '—'}</p>
                </div>
              )}
              {selected.manager_comment && (
                <div className="p-3 rounded-md border bg-muted/30 text-xs">
                  <p className="font-medium text-blue-600">Manager: {selected.manager_name}</p>
                  <p className="mt-1">{selected.manager_comment}</p>
                </div>
              )}
              {selected.hr_comment && (
                <div className="p-3 rounded-md border bg-muted/30 text-xs">
                  <p className="font-medium text-green-700">HR: {selected.hr_name}</p>
                  <p className="mt-1">{selected.hr_comment}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
